import moment from "moment";
export var MyMealsMenuTexts;
(function (MyMealsMenuTexts) {
    MyMealsMenuTexts["ERROR_FETCHING_ORDERS"] = "Error fetching orders: ";
    MyMealsMenuTexts["MENU_LOADING"] = "Subscription #000000";
    MyMealsMenuTexts["NO_ORDERS_TO_DISPLAY"] = "No orders to display.";
    MyMealsMenuTexts["NO_SUBSCRIPTIONS"] = "No Subscriptions to Display.";
    MyMealsMenuTexts["ORDER"] = "Order";
    MyMealsMenuTexts["ORDER_FOR"] = "Order for";
    MyMealsMenuTexts["ORDER_FOR_NEXT"] = "Order for next";
    MyMealsMenuTexts["ORDER_FOR_TODAY"] = "Order for today";
    MyMealsMenuTexts["ORDER_FOR_TOMORROW"] = "Order for tomorrow";
    MyMealsMenuTexts["ORDER_FROM_YESTERDAY"] = "Order from yesterday";
    MyMealsMenuTexts["ORDERED_LAST"] = "Ordered last";
    MyMealsMenuTexts["ORDERED_ON"] = "Ordered on";
    MyMealsMenuTexts["SOMETHING_WENT_WRONG"] = "Something went wrong while looking for your orders.";
    MyMealsMenuTexts["START_DATE_UNAVAILABLE"] = "Start date unavailable.";
    MyMealsMenuTexts["STARTED_LAST"] = "Started last";
    MyMealsMenuTexts["STARTED_ON"] = "Started on";
    MyMealsMenuTexts["STARTED_TODAY"] = "Started today";
    MyMealsMenuTexts["STARTED_YESTERDAY"] = "Started yesterday";
    MyMealsMenuTexts["STARTING_NEXT"] = "Starting next";
    MyMealsMenuTexts["STARTING_ON"] = "Starting on";
    MyMealsMenuTexts["STARTING_TOMORROW"] = "Starting tomorrow";
    MyMealsMenuTexts["SUBSCRIPTION"] = "Subscription";
})(MyMealsMenuTexts || (MyMealsMenuTexts = {}));
// Define date formats for each condition
const formats = {
    today: "",
    tomorrow: "",
    yesterday: "",
    nextWeek: "dddd",
    lastWeek: "dddd",
    default: "MMM D, YYYY", // "MMM D, YYYY" is the format for the date (e.g. "Jan 1, 2022")
};
// ** ==================== HELPER FUNCTIONS ====================
export const getCustomDateText = (date, isOrder) => {
    // Validate the date string
    const momentDate = moment(date, moment.ISO_8601, true);
    if (!momentDate.isValid()) {
        return MyMealsMenuTexts.START_DATE_UNAVAILABLE;
    }
    const today = moment();
    // Helper function to format date with labels
    const getDateText = (label, format) => `${label} ${momentDate.format(format)}`;
    // Condition 1: Date is today
    // Example Output (isOrder: "Order"): Any date that matches today would output "Order for today"
    // Example Output (!isOrder: "Subscription"): Any date that matches today would output "Started today"
    if (momentDate.isSame(today, "day")) {
        return isOrder ? MyMealsMenuTexts.ORDER_FOR_TODAY : MyMealsMenuTexts.STARTED_TODAY;
    }
    // Condition 2: Date is tomorrow
    // Example Output (isOrder: "Orders"): Any date that matches tomorrow would output "Order for tomorrow"
    // Example Output (!isOrder: "Subscription"): Any date that matches tomorrow would output "Starting tomorrow"
    else if (momentDate.isSame(today.clone().add(1, "days"), "day")) {
        return isOrder ? MyMealsMenuTexts.ORDER_FOR_TOMORROW : MyMealsMenuTexts.STARTING_TOMORROW;
    }
    // Condition 3: Date was yesterday
    // Example Output (isOrder: "Orders"): Any date that matches yesterday would output "Order from yesterday"
    // Example Output (!isOrder: "Subscription"): Any date that matches yesterday would output "Started yesterday"
    else if (momentDate.isSame(today.clone().subtract(1, "days"), "day")) {
        return isOrder ? MyMealsMenuTexts.ORDER_FROM_YESTERDAY : MyMealsMenuTexts.STARTED_YESTERDAY;
    }
    // Condition 4: Date is in the next week
    // Example Output (isOrder: "Orders"): Any date that falls within the next week would output "Order for next [dddd]"
    // Example Output (!isOrder: "Subscription"): Any date that falls within the next week would output "Starting next [dddd]"
    else if (momentDate.isAfter(today.clone().add(1, "days")) && momentDate.isBefore(today.clone().add(7, "days"))) {
        return isOrder
            ? getDateText(MyMealsMenuTexts.ORDER_FOR_NEXT, formats.nextWeek)
            : getDateText(MyMealsMenuTexts.STARTING_NEXT, formats.nextWeek);
    }
    // Condition 5: Date was in the past week
    // Example Output (isOrder: "Order"): Any date that falls within the past week would output "Ordered last [dddd]"
    // Example Output (!isOrder: "Subscription"): Any date that falls within the past week would output "Started last [dddd]"
    else if (momentDate.isBefore(today.clone().subtract(1, "days")) &&
        momentDate.isAfter(today.clone().subtract(7, "days"))) {
        return isOrder
            ? getDateText(MyMealsMenuTexts.ORDERED_LAST, formats.lastWeek)
            : getDateText(MyMealsMenuTexts.STARTED_LAST, formats.lastWeek);
    }
    // Condition 6: Any future or past date (more than a week)
    // Example Output (isOrder: "Orders"):
    //   Any future date would output "Order for [MMM D, YYYY]"
    //   Any past date would output "Ordered on [MMM D, YYYY]"
    // Example Output (!isOrder: "Subscriptions"):
    //   Any future date would output "Starting on [MMM D, YYYY]"
    //   Any past date would output "Started on [MMM D, YYYY]"
    else {
        return momentDate.isAfter(today)
            ? getDateText(isOrder ? MyMealsMenuTexts.ORDER_FOR : MyMealsMenuTexts.STARTING_ON, formats.default)
            : getDateText(isOrder ? MyMealsMenuTexts.ORDERED_ON : MyMealsMenuTexts.STARTED_ON, formats.default);
    }
};
// MY MEALS MENU: HANDLE CLICK FUNCTION
export const handleClick = (setAnchorEl) => (event) => {
    setAnchorEl(event.currentTarget);
};
// MY MEALS MENU: HANDLE CLOSE FUNCTION
export const handleClose = (setAnchorEl, setSelectedSubLinkId) => (subLinkId) => {
    subLinkId && setSelectedSubLinkId(subLinkId);
    setAnchorEl(null);
};
// RECIPE TYPE DISPLAY FUNCTION: Returns the display text for the recipe type name.
const getRecipeTypeNameDisplay = (name) => {
    return name ? `${name} - ` : "";
};
// ** ==================== FETCH HELPER FUNCTIONS ====================
// GROUP AND SORT ORDERS FUNCTION:
// This function groups orders by program, then by subscriptionLinkId (which ties orders to subscriptions).
// For each subscriptionLinkId, we prioritize keeping the order with SubscriptionStatus === 10 (Active).
export const groupOrdersByProgramAndSeparateBySubscription = (orders) => {
    // Return early if no valid orders are provided.
    if (!orders || !Array.isArray(orders) || orders.length === 0)
        return [];
    // Group orders by programName, then by subscriptionLinkId. For each subscription, we store only one order.
    const ordersGroupedByProgram = orders.reduce((groupAccumulator, order) => {
        const isOrder = order.SubscriptionStatus === 0;
        // Destructure the relevant fields from the order object.
        const { ProgramName: programName, SubscriptionLinkId: subscriptionLinkId, SubscriptionStatus, OrderNumber } = order;
        // If the program group doesn't exist in the accumulator, initialize it.
        if (!groupAccumulator[programName]) {
            groupAccumulator[programName] = {
                programName,
                entities: {}, // Each program will track orders grouped by subscriptionLinkId.
            };
        }
        // Get the existing order for this subscriptionLinkId, if any.
        const existingOrder = subscriptionLinkId ? groupAccumulator[programName].entities[subscriptionLinkId] : undefined;
        // ** MENU ITEM DISPLAY TEXTS **
        order.MenuItemDisplayText_OrderOrSubscription = isOrder ? MyMealsMenuTexts.ORDER : MyMealsMenuTexts.SUBSCRIPTION;
        // * Subscription Date *
        const dateToUse = isOrder ? order.StartDate : order.SubscriptionStartDate;
        order.MenuItemDisplayText_DisplayDate = getCustomDateText(dateToUse, isOrder);
        // * Recipe Type *
        const recipeTypeNameDisplay = isOrder
            ? getRecipeTypeNameDisplay(order.RecipeTypeName)
            : getRecipeTypeNameDisplay(order.SubscriptionRecipeTypeName);
        // * Subscription/Order Number *
        order.MenuItemDisplayText_SubscriptionOrOrderNumber = `${recipeTypeNameDisplay}${isOrder ? `#${OrderNumber}` : `#${subscriptionLinkId}`}`;
        // * PRIORITIZATION LOGIC *
        // Only replace the existing order for a subscription if:
        // 1. The current order has SubscriptionStatus === 10 (Active status, which is the highest priority).
        // 2. Or, there is no existing order for this subscription (first time adding an order for this subscriptionLinkId).
        if (SubscriptionStatus === 10 || !existingOrder) {
            if (subscriptionLinkId != null) {
                groupAccumulator[programName].entities[subscriptionLinkId] = order;
            }
        }
        return groupAccumulator; // Continue accumulating grouped orders.
    }, {});
    // Convert the accumulated grouped object into an array of programs,
    // and for each program, convert its entities (grouped orders by subscriptionLinkId) into an array.
    return Object.values(ordersGroupedByProgram).map((program) => ({
        programName: program.programName,
        entities: Object.keys(program.entities).map((subscriptionLinkId) => ({
            subscriptionLinkId,
            order: program.entities[subscriptionLinkId], // The final selected order for each subscription
        })),
    }));
};
